<template>
  <div>
    <!-- 使用公共导航栏组件 -->
    <Nav />

    <!-- 主要内容区域 -->
    <main class="mt-20 min-h-screen">
      <!-- 页面标题区域 -->
      <section class="bg-white py-8 border-b border-gray-200">
        <div class="max-w-7xl mx-auto px-6">
          <div class="flex items-center justify-between">
            <div>
              <h1 class="text-3xl font-bold text-gray-900 mb-2">帮助中心</h1>
              <p class="text-gray-600">常见问题解答，助您快速上手平台功能</p>
            </div>
          </div>
        </div>
      </section>
      <!-- 内容区域 -->
      <div class="max-w-7xl mx-auto px-6 py-12">
        <div class="flex gap-8">
          <!-- 左侧帮助分类导航 -->
          <aside
            class="w-80 bg-white rounded-xl shadow-sm p-6 h-fit sticky top-28"
          >
            <!-- 搜索框 -->
            <div class="mb-6">
              <div class="relative">
                <input
                  type="text"
                  v-model="searchKeyword"
                  placeholder="搜索帮助文档或关键词"
                  class="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  @keyup.enter="handleSearch"
                />
                <div
                  class="absolute left-3 top-3 w-4 h-4 flex items-center justify-center"
                >
                  <i class="ri-search-line text-gray-400"></i>
                </div>
                <button
                  class="absolute right-3 top-2 bg-primary text-white px-3 py-1 rounded-md text-sm"
                  @click="handleSearch"
                >
                  搜索
                </button>
              </div>
            </div>

            <!-- 帮助分类导航 -->
            <div class="mb-6">
              <h3 class="font-semibold text-gray-900 mb-4">帮助分类</h3>
              <nav class="space-y-1">
                <div
                  v-for="category in helpCategories"
                  :key="category.id"
                  class="mb-4"
                >
                  <button
                    class="w-full flex items-center justify-between text-left py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                    :class="{
                      'bg-primary/10 text-primary':
                        activeCategory === category.id,
                    }"
                    @click="toggleCategory(category.id)"
                  >
                    <span>{{ category.name }}</span>
                    <i
                      class="ri-arrow-down-s-line text-xs transition-transform"
                      :class="{
                        'transform rotate-180': expandedCategories.includes(
                          category.id
                        ),
                      }"
                    ></i>
                  </button>

                  <!-- 子分类 -->
                  <div
                    v-if="
                      expandedCategories.includes(category.id) &&
                      category.subcategories &&
                      category.subcategories.length > 0
                    "
                    class="pl-4 mt-2 border-l-2 border-gray-100 space-y-1"
                  >
                    <button
                      v-for="subcategory in category.subcategories"
                      :key="subcategory.id"
                      class="block w-full text-left py-2 px-3 text-sm text-gray-600 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors"
                      :class="{
                        'text-primary font-medium':
                          activeSubcategory === subcategory.id,
                      }"
                      @click="selectSubcategory(subcategory.id)"
                    >
                      {{ subcategory.name }}
                    </button>
                  </div>
                </div>
              </nav>
            </div>

            <!-- 联系客服 -->
            <div class="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <h4 class="font-medium text-gray-900 mb-2">需要更多帮助？</h4>
              <p class="text-sm text-gray-600 mb-4">
                如有其他问题，请联系我们的客服团队
              </p>
              <button
                class="w-full bg-primary text-white py-2 rounded-lg hover:bg-primary/90 transition-colors text-sm"
              >
                <i class="ri-headset-line mr-1"></i> 联系客服
              </button>
            </div>
          </aside>

          <!-- 右侧帮助内容区域 -->
          <div class="flex-1">
            <!-- 帮助文档列表 -->
            <div class="space-y-6">
              <!-- 无数据状态 -->
              <div class="bg-white rounded-xl shadow-sm p-12 text-left">
                <h3 class="text-lg font-medium text-gray-900 mb-2">
                  <i class="ri-information-line mr-2 text-gray-500"></i>
                  {{ helpDetail.title }}
                </h3>
                <!-- 横线 -->
                <hr class="my-6 border-t border-gray-200" />
                <div
                  v-html="helpDetail.content"
                  class="text-gray-600 mb-4"
                ></div>
              </div>
            </div>

            <!-- 加载状态 -->
            <div
              v-if="loading"
              class="bg-white rounded-xl shadow-sm p-12 text-center"
            >
              <div class="w-20 h-20 mx-auto mb-4">
                <i
                  class="ri-loader-line text-4xl text-primary animate-spin"
                ></i>
              </div>
              <p class="text-gray-600">加载中...</p>
            </div>
          </div>
        </div>
      </div>
    </main>
    <!-- 使用公共页脚组件 -->
    <Footer />
  </div>
</template>

<script>
import { Message } from "element-ui";
import Nav from "@/components/Nav.vue";
import Footer from "@/components/Footer.vue";
import Pagination from "@/components/Pagination.vue";
import api from "@/utils/api.js";

export default {
  name: "HelpDetail",
  components: {
    Nav,
    Footer,
    Pagination,
  },
  data() {
    return {
      // 帮助文章数据
      helpDetail: {},
      loading: false,

      // 分页信息
      totalCount: 0,
      page_current: 1,
      page_size: 8,

      // 搜索条件
      searchKeyword: "",
      activeCategory: "",
      activeSubcategory: "",
      expandedCategories: [], // 展开的分类

      helpCategories: [],

      // 热门问题（模拟数据）
      hotQuestions: [
        {
          id: "1",
          title: "如何注册和激活账号？",
          description: "详细介绍账号注册流程和激活步骤，帮助新用户快速上手。",
        },
        {
          id: "2",
          title: "如何申请和下载电子发票？",
          description:
            "指导用户如何在平台上申请发票以及下载电子发票的具体操作。",
        },
        {
          id: "3",
          title: "忘记密码如何找回？",
          description: "提供多种密码找回方式，确保用户账号安全。",
        },
      ],
    };
  },
  computed: {
    // 当前分类名称
    currentCategoryName() {
      if (this.activeSubcategory) {
        // 查找子分类
        for (let category of this.helpCategories) {
          const sub = category.subcategories.find(
            (s) => s.id === this.activeSubcategory
          );
          if (sub) return sub.name;
        }
      }
      if (this.activeCategory) {
        // 查找主分类
        const category = this.helpCategories.find(
          (c) => c.id === this.activeCategory
        );
        if (category) return category.name;
      }
      return "所有帮助文档";
    },

    // 当前分类描述
    currentCategoryDescription() {
      if (this.activeCategory) {
        const category = this.helpCategories.find(
          (c) => c.id === this.activeCategory
        );
        if (category) return category.description;
      }
      return "浏览所有帮助文档";
    },
  },
  mounted() {
    // 初始化加载帮助分类列表
    this.loadHelpCategories();
    this.loadHelpDetail(); // 初始加载帮助文档
  },
  methods: {
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString("zh-CN", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
      } catch (e) {
        return dateStr;
      }
    },

    // 切换分类展开/折叠
    toggleCategory(categoryId) {
      if (this.expandedCategories.includes(categoryId)) {
        this.expandedCategories = this.expandedCategories.filter(
          (id) => id !== categoryId
        );
      } else {
        this.expandedCategories.push(categoryId);
      }
    },

    // 选择子分类
    selectSubcategory(subcategoryId) {
      // 跳转至列表页
      this.$router.push({
        name: "Help",
        params: { subcategory_id: subcategoryId },
      });
    },

    // 处理搜索
    handleSearch() {
      this.$router.push({
        name: "Help",
        params: { keyword: this.searchKeyword.trim() },
      });
    },

    // 重置筛选条件
    resetFilters() {
      this.searchKeyword = "";
      this.activeCategory = "";
      this.activeSubcategory = "";
      this.page_current = 1;
      this.loadHelpArticles();
    },

    // 跳转到问题详情页
    goToQuestionDetail(questionId) {
      // 在实际项目中跳转到详情页
      this.$router.push(`/help/question/${questionId}`);
    },

    // loadHelpCategories 加载分类
    async loadHelpCategories() {
      try {
        const response = await api.help.getCategoryList();
        this.helpCategories = response.data.helpCategories || [];
      } catch (error) {
        console.error("获取帮助分类列表失败:", error);
        this.helpCategories = [];
      }
    },

    // 加载帮助文章
    async loadHelpDetail() {
      try {
        this.loading = true;

        // 构建请求参数
        const params = {
          id: this.$route.params.id,
        };

        // 添加搜索关键词
        if (this.searchKeyword.trim()) {
          params.keyword = this.searchKeyword.trim();
        }

        // 添加分类筛选条件
        if (this.activeCategory) {
          params.category_id = this.activeCategory;
        }

        // 添加子分类筛选条件
        if (this.activeSubcategory) {
          params.subcategory_id = this.activeSubcategory;
        }
        const response = await api.help.getDetail(params);
        this.helpDetail = response.data || {};
      } catch (error) {
        console.error("获取帮助文章列表失败:", error);
        this.helpDetail = {};
        this.totalCount = 0;
        Message.error("加载帮助文档失败，请稍后重试");
      } finally {
        this.loading = false;
      }
    },

  },
};
</script>

<style scoped>
/* 帮助中心样式 */
.help-categories {
  border-radius: 8px;
  overflow: hidden;
}

.category-item {
  transition: all 0.2s ease;
}

.category-item:hover {
  background-color: rgba(24, 144, 255, 0.05);
}

.subcategory-list {
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.article-card {
  transition: box-shadow 0.3s ease, transform 0.2s ease;
}

.article-card:hover {
  transform: translateY(-2px);
}
</style>



// WEBPACK FOOTER //
// src/pages/mainpage/HelpDetail.vue